/** ArtifactProvenanceSection — source file metadata + extraction log + markdown preview */

import { useTranslation } from 'react-i18next'
import { FileText, Clock, HardDrive, AlertCircle } from 'lucide-react'
import { cn } from '../../lib/utils'
import { useArtifactMarkdown } from './use-artifact-detail'

interface Props {
  uploadId: string
  filename: string
  contentType: string
  sizeBytes: number
  uploadedAt: string
  extractionStatus: string | null
  markdownState: string
  isDark: boolean
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

export function ArtifactProvenanceSection({
  uploadId,
  filename,
  contentType,
  sizeBytes,
  uploadedAt,
  extractionStatus,
  markdownState,
  isDark,
}: Props) {
  const { t } = useTranslation('artifact')
  const { data: markdown, isLoading: markdownLoading } = useArtifactMarkdown(uploadId, markdownState)

  const rowClass = cn('flex items-start gap-2 py-1.5 text-xs')
  const labelClass = cn('w-32 shrink-0 font-medium', isDark ? 'text-neutral-400' : 'text-neutral-500')
  const valueClass = cn('flex-1 break-all', isDark ? 'text-neutral-200' : 'text-neutral-800')

  return (
    <section aria-label={t('provenance.title')} className="space-y-3">
      <h3 className={cn('text-xs font-semibold uppercase tracking-wide', isDark ? 'text-neutral-400' : 'text-neutral-500')}>
        {t('provenance.title')}
      </h3>

      <div className={cn('rounded-lg border p-3 space-y-0.5', isDark ? 'border-white/[0.06] bg-surface-2' : 'border-neutral-200 bg-neutral-50')}>
        {/* Filename */}
        <div className={rowClass}>
          <span className={labelClass}>
            <FileText className="inline h-3 w-3 mr-1 opacity-70" />
            {t('provenance.filename')}
          </span>
          <span className={cn(valueClass, 'font-mono text-[11px]')}>{filename}</span>
        </div>

        {/* Content type */}
        <div className={rowClass}>
          <span className={labelClass}>{t('provenance.contentType')}</span>
          <span className={cn(valueClass, 'font-mono text-[11px]')}>{contentType}</span>
        </div>

        {/* Size */}
        <div className={rowClass}>
          <span className={labelClass}>
            <HardDrive className="inline h-3 w-3 mr-1 opacity-70" />
            {t('provenance.size')}
          </span>
          <span className={valueClass}>{formatBytes(sizeBytes)}</span>
        </div>

        {/* Uploaded at */}
        <div className={rowClass}>
          <span className={labelClass}>
            <Clock className="inline h-3 w-3 mr-1 opacity-70" />
            {t('provenance.uploadedAt')}
          </span>
          <span className={valueClass}>{formatDate(uploadedAt)}</span>
        </div>

        {/* Extraction status */}
        <div className={rowClass}>
          <span className={labelClass}>{t('provenance.extractionStatus')}</span>
          <ExtractionStatusBadge status={extractionStatus} isDark={isDark} />
        </div>
      </div>

      {/* Markdown preview */}
      <div className={cn('rounded-lg border', isDark ? 'border-white/[0.06]' : 'border-neutral-200')}>
        <div className={cn('border-b px-3 py-2 text-[11px] font-medium', isDark ? 'border-white/[0.06] text-neutral-400' : 'border-neutral-200 text-neutral-500')}>
          {t('provenance.markdownPreview')}
        </div>
        <div className={cn('max-h-40 overflow-y-auto p-3 text-[11px] leading-relaxed font-mono', isDark ? 'text-neutral-300' : 'text-neutral-700')}>
          {markdownLoading && (
            <span className="text-neutral-400">{t('provenance.loadingPreview')}</span>
          )}
          {!markdownLoading && markdown && (
            <pre className="whitespace-pre-wrap break-words">{markdown.slice(0, 1000)}{markdown.length > 1000 ? '…' : ''}</pre>
          )}
          {!markdownLoading && !markdown && (
            <span className={cn('flex items-center gap-1.5', isDark ? 'text-neutral-500' : 'text-neutral-400')}>
              <AlertCircle className="h-3 w-3" />
              {t('provenance.noMarkdown')}
            </span>
          )}
        </div>
      </div>
    </section>
  )
}

// ── Internal: ExtractionStatusBadge ───────────────────────────────────────────

function ExtractionStatusBadge({ status, isDark }: { status: string | null; isDark: boolean }) {
  const statusColors: Record<string, string> = {
    completed: 'text-green-500',
    failed: 'text-red-400',
    processing: 'text-yellow-400',
    pending: 'text-neutral-400',
    unsupported: 'text-neutral-400',
  }
  const color = statusColors[status ?? 'pending'] ?? 'text-neutral-400'
  return (
    <span className={cn('font-mono text-[11px]', color, isDark ? '' : '')}>
      {status ?? 'pending'}
    </span>
  )
}
